import type { Iti } from 'intl-tel-input';

import type { Nullable } from '@src/shared/types/common';

import { highlightFieldWithErrorByName, normalizeValidationErrors, showValidationErrorsSafely } from './validation-popup';

/**
 * Серверный канал `ModelState`: сервер шлёт только тексты — вызывается из `.cshtml` (57 мест), оформление своё.
 * `subclass` передаёт только `_WndNewContract.cshtml`, чтобы различать поля двух открытых окон по префиксу `wndNewContract_`.
 * Подсветка поля — общее ядро обоих каналов (`highlightFieldWithErrorByName` в `./validation-popup`): blur и сервер дают одно состояние.
 * @returns `false`, если ошибки есть.
 */
function checkValidationErrors(errors: Record<string, unknown> | undefined, subclass?: string): boolean {
    if (!errors || !Object.keys(errors).length) {
        return true;
    }

    for (const field of normalizeValidationErrors(errors).fields) {
        highlightFieldWithErrorByName(subclass ? subclass + field : field);
    }

    showValidationErrorsSafely(errors);

    return false;
}

function validateForecast(labelName: string) {
    // контролируем, чтобы пользователь мог вводить только цифры и символ ':'
    const sText1 = window.getTextFromComboBox(labelName);
    const str1 = sText1?.replace(/[^0-9:]+/g, '') ?? '';

    window.setTextToComboBox(labelName, str1);

    //обрезаем все что больше 6 символов
    while ((window.getTextFromComboBox(labelName) ?? '').length > 6) {
        const sText2 = window.getTextFromComboBox(labelName);
        const str2 = sText2?.slice(0, sText2.length - 1) ?? '';
        window.setTextToComboBox(labelName, str2);
    }

    //контролируем, чтобы вводили только один символ ':' после 2 или 3 символа
    let i = 0;
    const searhChar = ':';
    let strRes = '';
    const str = window.getTextFromComboBox(labelName);
    if (!str) {
        return;
    }
    while (i < str.length) {
        if ((str[i] !== searhChar) || ((str[i] === searhChar && i === 2) || (i === 3 && str[i - 1] !== searhChar))) {
            strRes += str[i];
        }
        i++;
    }
    window.setTextToComboBox(labelName, strRes);

    // ищем : и проверяем, чтобы не ввели всякую ерунду после : может быть только максимум 59
    const str3 = window.getTextFromComboBox(labelName);
    if (!str3) {
        return;
    }
    const index = str3.indexOf(searhChar);

    if (index !== -1) {
        let j = 0;
        let strRes2 = '';

        while (j < str3.length) {
            if (((j <= index) || ((j === index + 1) && (parseInt(str3[j]) <= 5))) || ((j === index + 2) && (parseInt(str3[j - 1]) <= 5))) {
                strRes2 += str3[j];
            }
            j++;
        }
        window.setTextToComboBox(labelName, strRes2);
    }
}

function validatePhoneInput(iti: Iti | undefined) {
    $(this).kendoValidator({
        validationSummary: undefined,
        rules: {
            phoneValidation: () => {
                if (!iti?.isValidNumber()) {
                    this.value = ' ';
                    return false;
                }
                return true;
            }
        },
        messages: {
            phoneValidation: 'Неверный телефон'
        }
    });
}

function validatePhoneTextBox(inputElement: JQuery<HTMLInputElement>, regexp: RegExp) {
    const parentElement = inputElement.closest('.k-textbox, .k-combobox');
    if (!parentElement.find('.k-input-validation-icon').length) {
        parentElement.append(`
            <span class="k-input-validation-icon k-icon k-i-warning k-hidden"></span>
        `);
    }

    const validationIcon = parentElement.find('.k-input-validation-icon');
    inputElement.on('input', ({ currentTarget }) => {
        const value = currentTarget.value;

        if (value) {
            currentTarget.value = value.replace(regexp, (match) => {
                const hasError = Boolean(match);
                parentElement.toggleClass('k-invalid', hasError);
                validationIcon.toggleClass('k-hidden', !hasError);
                return '';
            }).trim();

            /**
             * Анимированно удаляем иконку
             * */
            setTimeout(() => {
                parentElement.removeClass('k-invalid');
                validationIcon.addClass('k-hidden');
            }, 100);
        }
    });
}

/**
 * Запрещённое имя лида: точное совпадение со списком без учёта регистра или строка только из цифр.
 * Несколько пробелов подряд схлопываются так же, как серверный `RemoveSpaces`. Пустое имя не запрещено — это отдельная проверка.
 */
function validateLeadName(name: Nullable<string>, forbiddenNames: readonly string[]): boolean {
    const leadName = (name ?? '').trim().replace(/\s+/g, ' ');
    if (!leadName) {
        return false;
    }

    const normalized = leadName.toLowerCase();
    return forbiddenNames.some((forbiddenName) => forbiddenName.toLowerCase() === normalized) || /^\d+$/.test(leadName);
}

/**
 * Снимает атрибут `data-val-required` с одного или нескольких элементов формы,
 * отключая тем самым клиентскую валидацию обязательности поля.
 *
 * Каждый аргумент может быть:
 * - **строкой-id** (`'Post'`) — автоматически превращается в `#Post`;
 * - **CSS-селектором** (`'#MyField'`, `'.some-input'`);
 * - **jQuery-объектом** (`$('#MyField')`, `tickets`);
 * - **HTMLElement** (`document.getElementById('MyField')`).
 *
 * @param targets - Один или несколько id / селекторов / jQuery-объектов / DOM-элементов.
 *
 * @example
 * removeValRequired('Post', 'AdaptationStep', 'KnowledgeBaseDocuments');
 *
 * @example
 * removeValRequired(tickets, objectInput, ContactInfoType.element);
 */
function removeValRequired(...targets: Array<string | JQuery | HTMLElement>): void {
    for (const target of targets) {
        if (typeof target === 'string') {
            const selector = target.startsWith('#') || target.startsWith('.') ? target : `#${target}`;
            $(selector).removeAttr('data-val-required');
        }
        else {
            $(target).removeAttr('data-val-required');
        }
    }
}

window.validateForecast = validateForecast;
window.checkValidationErrors = checkValidationErrors;
window.validatePhoneInput = validatePhoneInput;
window.validatePhoneTextBox = validatePhoneTextBox;
window.validateLeadName = validateLeadName;
window.removeValRequired = removeValRequired;
